import React from 'react';

import Button from '../../Button';
import Icon from '../../Icon';
import type { ColumnMeta } from '../types';
import { getStickyStyle, tableHeaderVariants } from './tableStyles';

type TableHeaderProps<T> = {
  columns: ColumnMeta<T>[];
  sort?: SortState;
  onSort?: (newSort: string) => void;
  headersClassName?: string;
  stickyHeader?: boolean;
  readonly?: boolean;
};

const TableHeader = <T,>({
  columns,
  sort,
  onSort,
  headersClassName,
  stickyHeader = true,
  readonly = false,
}: TableHeaderProps<T>) => {
  const styles = tableHeaderVariants({ stickyHeader });

  return (
    <thead className={styles.head()}>
      <tr className={styles.row({ class: headersClassName })}>
        {columns.map(column => {
          const { name, direction: dir } = sort ?? {};
          const sortName = column.sortName;
          const isSortable = Boolean(sortName) && !readonly;
          const showSort = isSortable && name === sortName && !!dir;
          const align = column.align ?? 'left';
          const stickySide = column.stickySide ?? 'none';
          const content = (
            <>
              <span className={styles.label()}>{column.header}</span>
              {showSort ? (
                <Icon
                  icon={dir === 'asc' ? 'up' : 'down'}
                  className={styles.icon()}
                />
              ) : null}
            </>
          );

          return (
            <th
              key={column.id ?? column.columnIndex}
              style={getStickyStyle(column, column.headerStyle)}
              className={styles.cell({
                align,
                stickySide,
              })}
              scope="col"
            >
              {isSortable ? (
                <Button
                  type="ghost"
                  rounded={false}
                  width="full"
                  className={styles.button({
                    align,
                    sortable: true,
                    readonly,
                  })}
                  onClick={() => onSort?.(sortName!)}
                >
                  {content}
                </Button>
              ) : (
                <div
                  className={styles.button({
                    align,
                    sortable: false,
                    readonly,
                  })}
                >
                  {content}
                </div>
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

export default React.memo(TableHeader) as typeof TableHeader;
